import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Group, Loader, Text } from '@mantine/core';
import { Sparkles } from 'lucide-react';
import { SurfaceCard } from '@/design/SurfaceCard';
import type { AdHocAggregateResult } from '@/shared/analytics/AnalyticsTypes';
import { metricLabel, primaryKeyLabel } from '@/shared/analytics/MetricFormatting';
import type { TopicDefinition } from './TopicCatalogTypes';
import type { TopicCallout } from './TopicNarration';
import CalloutList from './CalloutList';
import { fetchTopicInsights } from './TopicInsightsClient';
import type { TopicInsightsRequest } from '@/shared/assist/TopicInsightsContract';

const REQUEST_ROW_LIMIT = 12;

interface TopicInsightsPanelProps {
    surfaceTitle: string;
    definition: TopicDefinition;
    primary: AdHocAggregateResult;
    /** Client-derived callouts shown instantly and kept when the LLM call fails. */
    derived: TopicCallout[];
}

function buildRequest(surfaceTitle: string, definition: TopicDefinition, primary: AdHocAggregateResult): TopicInsightsRequest {
    const total = primary.totals[definition.headlineMetric];
    return {
        surfaceTitle,
        topicTitle: definition.title,
        question: definition.question,
        lensLabel: definition.lensLabel,
        metricLabel: metricLabel(definition.headlineMetric),
        totals: { ...primary.totals },
        rows: primary.rows.slice(0, REQUEST_ROW_LIMIT).map((row, index) => {
            const raw = row.keys[definition.primaryField] ?? '—';
            const value = row.metrics[definition.headlineMetric];
            return {
                label: primaryKeyLabel(definition.primaryField, raw, index),
                value,
                share: total > 0 ? value / total : 0,
            };
        }),
    };
}

/**
 * The drill-in side rail's "Insights" card. Renders the client-derived
 * callouts immediately, then asks the BFF's LLM topic-insights route for
 * richer bullets and swaps them in when they arrive; a failed or empty reply
 * silently keeps the derived set.
 */
export default function TopicInsightsPanel({ surfaceTitle, definition, primary, derived }: TopicInsightsPanelProps): ReactElement | null {
    const [generated, setGenerated] = useState<TopicCallout[] | null>(null);
    const [generating, setGenerating] = useState(false);

    const request = useMemo(() => buildRequest(surfaceTitle, definition, primary), [surfaceTitle, definition, primary]);

    useEffect((): (() => void) => {
        let cancelled = false;
        setGenerated(null);
        if (request.rows.length === 0) return () => undefined;
        setGenerating(true);
        void fetchTopicInsights(request)
            .then((reply) => {
                if (!cancelled && reply.insights.length > 0) setGenerated(reply.insights);
            })
            .catch(() => undefined)
            .finally(() => {
                if (!cancelled) setGenerating(false);
            });
        return (): void => {
            cancelled = true;
        };
    }, [request]);

    const shown = generated ?? derived;
    if (shown.length === 0 && !generating) return null;

    return (
        <SurfaceCard $padding="md">
            <Group gap={6} mb="sm" wrap="nowrap" justify="space-between">
                <Group gap={6} wrap="nowrap">
                    <Sparkles size={14} style={{ color: 'var(--mantine-primary-color-filled)' }} />
                    <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
                        Insights
                    </Text>
                </Group>
                {generating && (
                    <Group gap={6} wrap="nowrap">
                        <Loader size={10} />
                        <Text size="xs" c="dimmed">
                            generating…
                        </Text>
                    </Group>
                )}
            </Group>
            <CalloutList callouts={shown} accent={definition.accent} />
        </SurfaceCard>
    );
}
